import { Button } from '@alifd/next';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import type {
  ChargingPileGroupDataApiParams,
  ChargingPileGroupDataResData,
} from '@/types/api/chargingPile/chargingPileGroupData';
import type { MqttGroupData } from '@/types/database/chargingPile/chargingPileMqttData';

import { MqttContext } from '../../../MqttContextProvider';
import { GroupContext } from '../../../PileCtxProvider';
import GroupPileStatusPie from './components/GroupPileStatusPie';
import NameWithPath from './components/NameWithPath';
import UsageLabelAndValue from './components/UsageLabelAndValue';
import WaitingModal from './components/WaitingModal';
import { Container } from './styles';

type keys = keyof ChargingPileGroupDataResData;

type CurrentGroupData = {
  [key in keys]: ChargingPileGroupDataResData[key] | '無資料';
};

const CurrentGroupCard: React.FC = () => {
  const { currentGroup, setChart } = useContext(GroupContext);
  const { mqttData } = useContext(MqttContext);
  const [currentName, setCurrentName] = useState<string>('');
  const [currentGroupMqttData, setCurrentGroupMqttData] =
    useState<MqttGroupData>();
  const [groupData, setGroupData] = useState<CurrentGroupData>({
    peakMonthlyEnergyGap: '無資料',
    peakMonthlyExpense: '無資料',
    offPeakMonthlyExpense: '無資料',
    offPeakMonthlyEnergyGap: '無資料',
    monthlyTotalEnergyGap: '無資料',
    monthlyTotalExpense: '無資料',
  });

  const getGroupData = useCallback(async (id: string) => {
    const params: ChargingPileGroupDataApiParams = {
      timeStamp: moment().valueOf(),
      groupId: id,
    };
    try {
      const res = await axios.get<ChargingPileGroupDataResData>(
        '/ezcon/api/chargingPileGroupData',
        {
          params,
          headers: {
            authorization: localStorage.getItem('feathers-jwt'),
          },
        }
      );
      setGroupData(res.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    if (currentGroup?._id) {
      getGroupData(currentGroup._id);
      setCurrentName(`${currentGroup.name}-${currentGroup._id}`);
    }
  }, [currentGroup, getGroupData]);

  useEffect(() => {
    if (mqttData && currentName) {
      const groupData = mqttData?.groups?.[currentName];
      if (groupData) {
        setCurrentGroupMqttData(groupData);
      }
    }
  }, [mqttData, currentName]);

  return (
    <Container>
      <Box
        sx={{
          gridColumn: '1 / -1',
        }}
      >
        {currentGroup ? <NameWithPath /> : <Typography>無資料</Typography>}
      </Box>
      {currentGroup && (
        <>
          <GroupPileStatusPie status={currentGroupMqttData?.pileStatus} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              justifyItems: 'end',
              alignItems: 'end',
              flexGrow: '1',
            }}
          >
            <UsageLabelAndValue
              label="目前功率"
              value={currentGroupMqttData?.power || '0'}
              unit="瓩"
            />
            <UsageLabelAndValue
              label="本月尖峰用電量"
              value={groupData.peakMonthlyEnergyGap}
              unit="度"
            />
            <UsageLabelAndValue
              label="本月尖峰預估收入"
              value={groupData.peakMonthlyExpense}
              unit="元"
            />
            <UsageLabelAndValue
              label="本月離峰用電量"
              value={groupData.offPeakMonthlyEnergyGap}
              unit="度"
            />
            <UsageLabelAndValue
              label="本月離峰預估收入"
              value={groupData.offPeakMonthlyExpense}
              unit="元"
            />
            <UsageLabelAndValue
              label="本月總用電量"
              value={groupData.monthlyTotalEnergyGap}
              unit="度"
            />
            <UsageLabelAndValue
              label="本月總預估收入"
              value={groupData.monthlyTotalExpense}
              unit="元"
            />
            {currentGroup.type === 'circuit' && (
              <WaitingModal group={currentGroup} />
            )}
            <Button
              style={{
                gridColumn: '1/3',
                width: '100%',
              }}
              onClick={() => {
                if (currentGroup) {
                  setChart({
                    show: true,
                    type: 'group',
                    id: currentGroup._id,
                    name: currentGroup.name,
                  });
                }
              }}
            >
              查看用電圖表
            </Button>
          </Box>
        </>
      )}
    </Container>
  );
};

export default CurrentGroupCard;
